import { Link } from "react-router-dom";
import ClimbingAreasByState from "../components/climbingAreasByState";
import { BreadcrumbSchema } from "../components/structuredData";
import { useSeoMeta } from "../hooks/useSeoMeta";
import NotFoundPage from "./notFound";
import "../components/browse.css";

// State code to full name mapping
const stateNames: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming'
};

interface ForecastByStateAreasProps {
    code?: string
}

const ForecastByStateAreas = ({ code: rawCode }: ForecastByStateAreasProps) => {
    // Accept /forecast-by-state/ca as well as /CA; the canonical URL uses upper case
    const code = rawCode?.toUpperCase();
    const notFound = code !== undefined && !stateNames[code];
    const stateName = code ? stateNames[code] || code : 'All States';
    const title = notFound
      ? "Page not found - ClimbingWeather.com"
      : code
      ? `${stateName} Rock Climbing Weather - ClimbingWeather.com`
      : "All States - Rock Climbing Weather - ClimbingWeather.com";
    const description = code
      ? `Weather forecasts for rock climbing areas in ${stateName}. Find detailed hourly and daily forecasts for planning your climbing trips.`
      : "Browse rock climbing weather forecasts by state. Find detailed weather predictions for climbing areas across the United States.";

    // Runs after NotFoundPage's own useSeoMeta, so it carries noindex too
    useSeoMeta({
      title,
      description,
      canonicalPath: code ? `/forecast-by-state/${code}` : '/forecast-by-state',
      noIndex: notFound
    });

    if (notFound) {
      return <NotFoundPage title="We couldn’t find that state" message="State pages use two-letter codes, like /forecast-by-state/CO." />;
    }

    return (
        <div className="cw-page browse">
            <BreadcrumbSchema
              items={[
                { name: 'Home', url: 'https://climbingweather.com/' },
                { name: code ? stateName : 'All States', url: `https://climbingweather.com/forecast-by-state${code ? `/${code}` : ''}` }
              ]}
            />
            <div className="browse__body">
                <header className="browse__intro">
                    {code && (
                        <nav className="browse__crumbs" aria-label="Breadcrumb">
                            <Link to="/forecast-by-state">All states</Link>
                            <span aria-hidden="true"> › </span>
                            <span aria-current="page">{stateName}</span>
                        </nav>
                    )}
                    <h1 className="browse__title">{code ? `${stateName} climbing areas` : "Climbing areas by state"}</h1>
                    <p className="browse__lede">Choose an area to see its hourly and 7-day forecast.</p>
                </header>
                <ClimbingAreasByState code={code} />
            </div>
        </div>
    )

};

export default ForecastByStateAreas;
